"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCreatePresignedUrls = exports.useCreatePresignedUrl = exports.useUploadFiles = exports.useUploadFile = exports.useSecureFileUrl = exports.defaultPresignEndPoint = void 0;
const react_1 = require("react");
const lib_1 = require("../lib");
const Api_hooks_1 = require("./Api.hooks");
exports.defaultPresignEndPoint = 'files/presign';
function useSecureFileUrl(url) {
    if (url) {
        return `${url}&token=${lib_1.api.getAccessToken()}`;
    }
}
exports.useSecureFileUrl = useSecureFileUrl;
function useUploadFile(path, presignEndPoint = exports.defaultPresignEndPoint) {
    const { presignedResult, isLoading: isLoadingPresignedUrl, error: presignedError, presignUrl } = useCreatePresignedUrl(path, presignEndPoint);
    const [progress, setProgress] = (0, react_1.useState)(0);
    const [fileUrl, setFileUrl] = (0, react_1.useState)();
    const [file, setFile] = (0, react_1.useState)();
    const [response, setResponse] = (0, react_1.useState)();
    const [error, setError] = (0, react_1.useState)();
    const [isUploading, setIsUploading] = (0, react_1.useState)(false);
    const requestRef = (0, react_1.useRef)(new XMLHttpRequest());
    (0, react_1.useEffect)(() => {
        const request = requestRef.current;
        if (!isLoadingPresignedUrl && presignedResult && file) {
            request.upload.addEventListener('progress', event => {
                if (event.lengthComputable) {
                    setProgress(event.loaded / event.total);
                }
            });
            request.upload.addEventListener('load', event => {
                setProgress(1);
                setIsUploading(false);
                setFileUrl(presignedResult === null || presignedResult === void 0 ? void 0 : presignedResult.destinationUrl);
                setResponse(request.response);
            });
            request.upload.addEventListener('error', event => {
                setError(request.response);
                setIsUploading(false);
                setProgress(0);
            });
            request.open('PUT', presignedResult.signedUrl);
            request.setRequestHeader('Content-Type', file.type);
            request.responseType = 'text';
            request.send(file);
            return () => request.abort();
        }
    }, [file, isLoadingPresignedUrl, presignedResult]);
    const upload = (0, react_1.useCallback)((file) => {
        setIsUploading(true);
        setProgress(0);
        setFile(file);
        presignUrl(file.type);
    }, [presignUrl]);
    return {
        fileUrl,
        uploadedFile: file,
        isUploading,
        progress,
        response,
        error: error || presignedError,
        upload
    };
}
exports.useUploadFile = useUploadFile;
function useUploadFiles(path, presignEndPoint = exports.defaultPresignEndPoint) {
    const { presignedResults, error: presignedError, presignUrls } = useCreatePresignedUrls(path, presignEndPoint);
    const fileIndexRef = (0, react_1.useRef)(0);
    const [uploadFiles, setUploadFiles] = (0, react_1.useState)();
    const [isUploading, setIsUploading] = (0, react_1.useState)(false);
    const upload = (0, react_1.useCallback)((fileList) => {
        setIsUploading(true);
        const files = [];
        for (let i = 0; i < fileList.length; i++) {
            const uploadFile = {
                file: fileList[i],
                progress: 0,
                isUploading: true,
                request: new XMLHttpRequest(),
                isCompleted: false,
                isError: false
            };
            files.push(uploadFile);
        }
        // start upload (see first useEffect)
        setUploadFiles([...(uploadFiles || []), ...files]);
        presignUrls(files.map(u => u.file));
    }, [presignUrls, uploadFiles]);
    // remove an upload file and abort if uploading
    const remove = (0, react_1.useCallback)((file) => {
        if (uploadFiles) {
            const index = uploadFiles.findIndex(f => f.file === file);
            if (index > -1) {
                uploadFiles[index].request.abort();
                uploadFiles.splice(index, 1);
                if (index <= fileIndexRef.current) {
                    fileIndexRef.current--;
                }
                setUploadFiles([...uploadFiles]);
            }
        }
    }, [uploadFiles]);
    // get pre-signed url for files
    (0, react_1.useEffect)(() => {
        if (uploadFiles && fileIndexRef.current < uploadFiles.length) {
            // find presigned url matching the file to upload
            const uploadFile = uploadFiles[fileIndexRef.current];
            const presignedResult = presignedResults.find(p => p.file === uploadFile.file);
            if (!presignedResult)
                return; // have not yet received presigned url
            uploadFile.presignedResult = presignedResult;
            // console.log('presigned received uploadFile', uploadFiles);
            setUploadFiles([...uploadFiles]);
            fileIndexRef.current++;
            // Perform Upload
            uploadFile.request.upload.addEventListener('progress', event => {
                if (event.lengthComputable) {
                    uploadFile.progress = event.loaded / event.total;
                    // console.log('uploadFile progress');
                    setUploadFiles([...uploadFiles]);
                }
            });
            uploadFile.request.upload.addEventListener('load', event => {
                uploadFile.progress = 1;
                uploadFile.isUploading = false;
                uploadFile.isCompleted = true;
                // console.log('uploadFile load completed');
                setUploadFiles([...uploadFiles]);
            });
            uploadFile.request.upload.addEventListener('error', event => {
                uploadFile.progress = 0;
                uploadFile.isUploading = false;
                uploadFile.isError = true;
                // console.log('uploadFile error');
                setUploadFiles([...uploadFiles]);
            });
            uploadFile.request.open('PUT', uploadFile.presignedResult.signedUrl);
            uploadFile.request.setRequestHeader('Content-Type', uploadFile.file.type);
            uploadFile.request.responseType = 'text';
            uploadFile.request.send(uploadFile.file);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presignUrls, presignedResults]);
    (0, react_1.useEffect)(() => {
        // abort upload requests when unmounted
        return () => {
            // console.log('uploadfiles ABORT');
            if (uploadFiles) {
                uploadFiles.forEach(uploadFile => uploadFile.request.abort());
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return {
        uploadFiles,
        isUploading,
        error: presignedError,
        upload,
        remove
    };
}
exports.useUploadFiles = useUploadFiles;
/**
 * Get a single presigned url
 */
function useCreatePresignedUrl(path, presignEndPoint = exports.defaultPresignEndPoint) {
    const { isLoading, post, data, error } = (0, Api_hooks_1.useApiPost)(`${presignEndPoint}/${path}`);
    const presignUrl = (0, react_1.useCallback)((mimeType) => {
        post({ mimeType });
    }, [post]);
    return { presignedResult: data, isLoading, error, presignUrl };
}
exports.useCreatePresignedUrl = useCreatePresignedUrl;
/**
 * Support getting multiple presigned urls for bulk uploading
 */
function useCreatePresignedUrls(path, presignEndPoint = exports.defaultPresignEndPoint) {
    const { isLoading, post, data, error } = (0, Api_hooks_1.useApiPost)(`${presignEndPoint}/${path}`);
    const filesRef = (0, react_1.useRef)([]);
    const nextIndexRef = (0, react_1.useRef)(0);
    const [presignedResults, setPresignedResults] = (0, react_1.useState)([]);
    const presignUrls = (0, react_1.useCallback)((files) => {
        if (files) {
            // add files to get presigned urls
            filesRef.current.push(...files);
        }
        // get presigned url for next item in queue
        if (nextIndexRef.current < filesRef.current.length && !isLoading) {
            // console.log(
            //   'presignedUrls',
            //   nextIndexRef.current,
            //   filesRef.current[nextIndexRef.current]
            // );
            post({ mimeType: filesRef.current[nextIndexRef.current].type });
        }
    }, [isLoading, post]);
    (0, react_1.useEffect)(() => {
        // check if presigned url has returned and proceed to next file
        if (data && !isLoading) {
            const file = filesRef.current[nextIndexRef.current];
            setPresignedResults([...presignedResults, Object.assign(Object.assign({}, data), { file })]);
            nextIndexRef.current++;
            presignUrls(); // process next file
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, isLoading]);
    return { presignedResults, isLoading, error, presignUrls };
}
exports.useCreatePresignedUrls = useCreatePresignedUrls;
//# sourceMappingURL=File.hooks.js.map